'use client';

import { create } from 'zustand';

import { encodeFilterValue } from './encode';
import { appendSearchParamsToUrl, removeSearchParamsFromUrl, updateUrlState } from './url';

import type { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import type { MultiSelectOption } from '../components';
import type { FilterColumnsConfig } from './typed-filter-config';
import type { UrlUpdateMode } from './url';

/** URL sentinel for "user explicitly cleared all selections" on a filter with defaultValue */
export const MULTI_SELECT_CLEARED = '_all_';

export type FilterType = UrlUpdateMode;

// Store interfaces
interface TableFilterState {
  activeFilterParams: string[];
  activeParamsByOwner: Record<string, string[]>;
  filterType: FilterType;
  router: AppRouterInstance | null;
}

interface TableFilterActions {
  setActiveFilters: (filters: string[]) => void;
  setFilterType: (filterType: FilterType) => void;
  setRouter: (router: AppRouterInstance) => void;
  clearFilters: (filterParams?: string[]) => void;
  clearAllFilters: () => void;
  getActiveFilters: () => string[];
  /**
   * `owner` identifies one mounted `TableFilters`. Owners are tracked separately and
   * `activeFilterParams` is their union, so two instances on a page don't overwrite each other.
   */
  updateActiveFiltersFromConfig: (
    config: FilterColumnsConfig,
    searchParams: URLSearchParams | null,
    owner?: string
  ) => void;
  releaseActiveFilters: (owner: string) => void;
  updateFilterValue: (searchParam: string, value: string | null, searchParamsToClearOnChange?: string[]) => void;
  updateDateRangeFilter: (
    fromDateParam: string,
    toDateParam: string,
    fromDate: Date | null,
    toDate: Date | null,
    searchParamsToClearOnChange?: string[],
    preserveTime?: boolean
  ) => void;
  updateMultiSelectFilter: (
    searchParam: string,
    values: Record<string, string[]>,
    searchParamsToClearOnChange?: string[],
    groups?: { id: string; options?: MultiSelectOption[] }[]
  ) => void;
  updateMultiSelectFlatFilter: (
    searchParam: string,
    values: string[],
    searchParamsToClearOnChange?: string[],
    defaultValue?: readonly string[]
  ) => void;
}

// Zustand store
export const useTableFilterStore = create<TableFilterState & TableFilterActions>((set, get) => ({
  activeFilterParams: [],
  activeParamsByOwner: {},
  filterType: 'rsc',
  router: null,

  setActiveFilters: (filters) => {
    set({ activeFilterParams: filters });
  },

  setFilterType: (filterType) => {
    set({ filterType });
  },

  setRouter: (router) => {
    set({ router });
  },

  clearFilters: (filterParams) => {
    const paramsToRemove = filterParams || get().activeFilterParams;
    if (paramsToRemove.length === 0) return;

    const newUrl = removeSearchParamsFromUrl(window.location.href, paramsToRemove);
    const { filterType, router } = get();

    updateUrlState({ url: newUrl, mode: filterType, router });
  },

  clearAllFilters: () => {
    const { activeFilterParams } = get();
    get().clearFilters(activeFilterParams);
  },

  getActiveFilters: () => {
    return get().activeFilterParams;
  },

  releaseActiveFilters: (owner) => {
    const { [owner]: released, ...rest } = get().activeParamsByOwner;
    if (released === undefined) return;
    set({ activeParamsByOwner: rest, activeFilterParams: Array.from(new Set(Object.values(rest).flat())) });
  },

  updateActiveFiltersFromConfig: (config, searchParams, owner = 'default') => {
    const setIfChanged = (next: string[]) => {
      const { activeParamsByOwner } = get();
      const current = activeParamsByOwner[owner] ?? [];
      if (current.length === next.length && current.every((param, index) => param === next[index])) {
        return;
      }
      const byOwner = { ...activeParamsByOwner, [owner]: next };
      set({ activeParamsByOwner: byOwner, activeFilterParams: Array.from(new Set(Object.values(byOwner).flat())) });
    };

    if (!searchParams) {
      setIfChanged([]);
      return;
    }

    const allFilters = [...config.left, ...(config.right ?? [])];

    const activeParams = allFilters.reduce((acc, item) => {
      if ('searchParam' in item && item.searchParam && searchParams.has(item.searchParam)) {
        acc.push(item.searchParam);
      }
      if ('fromDateParam' in item && item.fromDateParam && searchParams.has(item.fromDateParam)) {
        acc.push(item.fromDateParam);
      }
      if ('toDateParam' in item && item.toDateParam && searchParams.has(item.toDateParam)) {
        acc.push(item.toDateParam);
      }
      return acc;
    }, [] as string[]);

    setIfChanged(activeParams);
  },

  /**
   * Update a single filter value in the URL
   */
  updateFilterValue: (searchParam, value, searchParamsToClearOnChange) => {
    const { filterType, router } = get();

    const encoded = encodeFilterValue(value);
    let newUrl: string;
    if (encoded !== null) {
      newUrl = appendSearchParamsToUrl(window.location.href, { [searchParam]: encoded });
    } else {
      newUrl = removeSearchParamsFromUrl(window.location.href, [searchParam]);
    }

    if (searchParamsToClearOnChange && searchParamsToClearOnChange.length > 0) {
      newUrl = removeSearchParamsFromUrl(newUrl, searchParamsToClearOnChange);
    }

    updateUrlState({ url: newUrl, mode: filterType, router });
  },

  /**
   * Update date range filters in the URL
   */
  updateDateRangeFilter: (
    fromDateParam,
    toDateParam,
    fromDate,
    toDate,
    searchParamsToClearOnChange,
    preserveTime = false
  ) => {
    const { filterType, router } = get();

    const params: Record<string, string> = {};
    const paramsToRemove: string[] = [];

    if (fromDate) {
      // Store as timestamp string for consistency with existing behavior
      params[fromDateParam] = fromDate.getTime().toString();
    } else {
      paramsToRemove.push(fromDateParam);
    }

    if (toDate) {
      if (preserveTime) {
        params[toDateParam] = toDate.getTime().toString();
      } else {
        // Set to end of day (23:59:59.999) so all events on the selected day are included
        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);
        params[toDateParam] = endOfDay.getTime().toString();
      }
    } else {
      paramsToRemove.push(toDateParam);
    }

    let newUrl = window.location.href;

    // Remove params that should be cleared
    if (paramsToRemove.length > 0) {
      newUrl = removeSearchParamsFromUrl(newUrl, paramsToRemove);
    }

    // Add new params
    if (Object.keys(params).length > 0) {
      newUrl = appendSearchParamsToUrl(newUrl, params);
    }

    // Clear additional params if specified
    if (searchParamsToClearOnChange && searchParamsToClearOnChange.length > 0) {
      newUrl = removeSearchParamsFromUrl(newUrl, searchParamsToClearOnChange);
    }

    updateUrlState({ url: newUrl, mode: filterType, router });
  },

  /**
   * Update multi-select filter in the URL
   */
  updateMultiSelectFilter: (searchParam, values, searchParamsToClearOnChange, groups) => {
    const { filterType, router } = get();

    // Convert object structure to URL param (comma-separated groupId:value pairs)
    const allEntries: string[] = [];

    Object.entries(values).forEach(([groupId, groupValues]) => {
      // If groups are provided, handle standalone groups differently
      if (groups) {
        const group = groups.find((g) => g.id === groupId);
        if (group && (!group.options || group.options.length === 0)) {
          // For standalone groups, just use the groupId
          if (groupValues.length > 0) {
            allEntries.push(groupId);
          }
        } else {
          // For groups with options, use groupId:optionValue format
          groupValues.forEach((value) => {
            allEntries.push(`${groupId}:${value}`);
          });
        }
      } else {
        // Default behavior without groups info
        groupValues.forEach((value) => {
          allEntries.push(`${groupId}:${value}`);
        });
      }
    });

    let newUrl: string;
    if (allEntries.length > 0) {
      newUrl = appendSearchParamsToUrl(window.location.href, { [searchParam]: allEntries.join(',') });
    } else {
      newUrl = removeSearchParamsFromUrl(window.location.href, [searchParam]);
    }

    if (searchParamsToClearOnChange && searchParamsToClearOnChange.length > 0) {
      newUrl = removeSearchParamsFromUrl(newUrl, searchParamsToClearOnChange);
    }

    updateUrlState({ url: newUrl, mode: filterType, router });
  },

  /**
   * Update flat multi-select filter in the URL (comma-separated values).
   * If the selected values match the defaultValue, removes the URL param to keep URLs clean.
   */
  updateMultiSelectFlatFilter: (searchParam, values, searchParamsToClearOnChange, defaultValue) => {
    const { filterType, router } = get();

    // If values match the default, remove from URL to keep it clean
    const isDefault =
      defaultValue &&
      values.length === defaultValue.length &&
      [...values].sort().every((v, i) => v === [...defaultValue].sort()[i]);

    const encoded = encodeFilterValue(values);
    let newUrl: string;
    if (encoded !== null && !isDefault) {
      newUrl = appendSearchParamsToUrl(window.location.href, { [searchParam]: encoded });
    } else if (values.length === 0 && defaultValue && defaultValue.length > 0) {
      // Explicitly cleared with a default present — write sentinel so page refresh
      // doesn't re-apply defaults. "none" is not a valid filter option value.
      newUrl = appendSearchParamsToUrl(window.location.href, { [searchParam]: MULTI_SELECT_CLEARED });
    } else {
      newUrl = removeSearchParamsFromUrl(window.location.href, [searchParam]);
    }

    if (searchParamsToClearOnChange && searchParamsToClearOnChange.length > 0) {
      newUrl = removeSearchParamsFromUrl(newUrl, searchParamsToClearOnChange);
    }

    updateUrlState({ url: newUrl, mode: filterType, router });
  },
}));
